import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/lib/env.config';
import { useBrand } from '@/shared/hooks/useBrand/useBrand';
import { useTheme } from '@/shared/theme/ThemeProvider/ThemeProvider';

import { type BrandMarkLogic } from './BrandMark.types';

/**
 * Which mark the shell draws (P28) — the admin twin of the phone's `BrandLogo`.
 *
 * The uploaded mark reaches the browser through the one public route that
 * resolves the setting and mints per request (`GET /media/brand-logo`, D10):
 * the id is only the cache key, never a storage url (N12), and the route owns
 * the dark → light fallback. Both failure shapes — nothing uploaded, or an
 * image that will not load — draw the mark `pnpm brand:assets` generated into
 * `public/brand/`, so a deployment always has a mark and never a broken-image
 * glyph. The name still labels it.
 */
export function useBrandMarkLogic(): BrandMarkLogic {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const { name, logoMediaId, logoDarkMediaId } = useBrand();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const id = theme === 'dark' ? (logoDarkMediaId ?? logoMediaId) : logoMediaId;
  const uploadedSrc =
    id === null ? null : `${env.apiBaseUrl}/media/brand-logo?variant=${theme}&v=${id}`;

  // A failure is remembered against ONE url, so a new upload or a theme switch
  // gets a fresh try without an effect resetting anything.
  const onError = useCallback(() => {
    setFailedSrc(uploadedSrc);
  }, [uploadedSrc]);

  return useMemo(() => {
    const isUploaded = uploadedSrc !== null && failedSrc !== uploadedSrc;

    return {
      src: isUploaded ? uploadedSrc : `/brand/logo-${theme}.png`,
      alt: name === '' ? t('shell.title') : name,
      isUploaded,
      onError,
    };
  }, [uploadedSrc, failedSrc, theme, name, t, onError]);
}
